import { Ionicons } from '@expo/vector-icons';
import { StackNavigationProp } from '@react-navigation/stack';
import Constants from 'expo-constants';
import { LinearGradient } from 'expo-linear-gradient';
import * as Updates from 'expo-updates';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  Animated,
  I18nManager,
  Linking,
  Modal,
  Platform,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AnimatedWave from '../components/AnimatedWave';
import { useCustomAlert } from '../components/CustomAlert';
import { changeLanguage as changeAppLanguage } from '../config/i18n';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { RootStackParamList } from '../navigation/RootNavigator';
import { alarmApi, authApi, deviceApi, siteApi, zoneApi } from '../services/api';
import { useProfileStats, UserStats } from '../hooks/queries/useProfile';
import logger from '../utils/logger';
import {
  canCreateReports,
  canManageSitesAndZones,
  canManageUsers,
  getRoleIcon,
  getRoleNameArabic,
  getRoleNameEnglish,
} from '../utils/rbac';

type ProfileScreenNavigationProp = StackNavigationProp<RootStackParamList, 'Main'>;

export default function ProfileScreen({ navigation }: { navigation: ProfileScreenNavigationProp }) {
  const { i18n } = useTranslation();
  const { theme, isDark, toggleTheme } = useTheme();
  const { user, logout, updateUser, checkAuth } = useAuth();
  const { showAlert, AlertComponent } = useCustomAlert();
  const insets = useSafeAreaInsets();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const canSeeStructureStats = canManageSitesAndZones(user);
  const {
    data: userStats = {
      devicesCount: 0,
      activeDevicesCount: 0,
      activeAlarmsCount: 0,
      sitesCount: 0,
      zonesCount: 0,
    },
    refetch: refetchStats,
    isRefetching,
  } = useProfileStats(user?.orgId ?? undefined, canSeeStructureStats);

  // Animation values
  const headerScale = useRef(new Animated.Value(0)).current;
  const controlsOpacity = useRef(new Animated.Value(0)).current;
  const shimmerAnim = useRef(new Animated.Value(0)).current;
  const twinkleAnim = useRef(new Animated.Value(0)).current;
  const shimmerLoopRef = useRef<Animated.CompositeAnimation | null>(null);
  const twinkleLoopRef = useRef<Animated.CompositeAnimation | null>(null);

  // Galaxy Stats
  const [stars] = useState(() =>
    Array.from({ length: 50 }).map((_, i) => ({
      id: i,
      top: Math.random() * 100,
      left: Math.random() * 100,
      size: Math.random() * 2 + 1,
      opacity: Math.random() * 0.5 + 0.3,
    })),
  );

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Edit profile state
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');

  const isArabic = i18n.language === 'ar';

  // Header animation on mount - REMOVED for cleaner experience
  useEffect(() => {
    // Initialize animations without animated entrance
    headerScale.setValue(1);
    controlsOpacity.setValue(1);

    // Shimmer animation - subtle effect
    shimmerLoopRef.current = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 3000,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 3000,
          useNativeDriver: true,
        }),
      ]),
    );
    shimmerLoopRef.current.start();

    // Twinkle Effect
    twinkleLoopRef.current = Animated.loop(
      Animated.sequence([
        Animated.timing(twinkleAnim, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: true,
        }),
        Animated.timing(twinkleAnim, {
          toValue: 0,
          duration: 2000,
          useNativeDriver: true,
        }),
      ]),
    );
    twinkleLoopRef.current.start();

    return () => {
      shimmerLoopRef.current?.stop();
      twinkleLoopRef.current?.stop();
    };
  }, []);

  // Load user statistics — handled by useProfileStats hook

  // Update firstName and lastName when user changes
  useEffect(() => {
    if (user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
    }
  }, [user]);

  const onRefresh = async () => {
    try {
      await checkAuth();
      refetchStats();
    } catch (error) {
      logger.error('Refresh failed:', error);
    }
  };

  const handleLogout = () => {
    showAlert(
      isArabic ? 'تسجيل الخروج' : 'Logout',
      isArabic ? 'هل أنت متأكد من تسجيل الخروج؟' : 'Are you sure you want to logout?',
      [
        { text: isArabic ? 'إلغاء' : 'Cancel', style: 'cancel' },
        {
          text: isArabic ? 'تسجيل الخروج' : 'Logout',
          style: 'destructive',
          onPress: async () => {
            await logout();
          },
        },
      ],
      'log-out',
      theme.colors.error,
    );
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showAlert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء ملء جميع الحقول' : 'Please fill all fields',
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'alert-circle',
        theme.colors.error,
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      showAlert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'كلمة المرور الجديدة غير متطابقة' : 'New passwords do not match',
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'alert-circle',
        theme.colors.error,
      );
      return;
    }

    if (newPassword.length < 6) {
      showAlert(
        isArabic ? 'خطأ' : 'Error',
        isArabic
          ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل'
          : 'Password must be at least 6 characters',
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'alert-circle',
        theme.colors.warning,
      );
      return;
    }

    try {
      setLoading(true);
      await authApi.changePassword(currentPassword, newPassword);
      showAlert(
        isArabic ? 'نجح' : 'Success',
        isArabic ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully',
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'checkmark-circle',
        theme.colors.success,
      );
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      showAlert(
        isArabic ? 'خطأ' : 'Error',
        error.response?.data?.message ||
          (isArabic ? 'فشل تغيير كلمة المرور' : 'Failed to change password'),
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'alert-circle',
        theme.colors.error,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = async () => {
    if (!firstName || !lastName) {
      showAlert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'الرجاء ملء جميع الحقول' : 'Please fill all fields',
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'alert-circle',
        theme.colors.error,
      );
      return;
    }

    try {
      setLoading(true);
      const updatedUser = await authApi.updateProfile({ firstName, lastName });
      await updateUser(updatedUser);
      showAlert(
        isArabic ? 'نجح' : 'Success',
        isArabic ? 'تم تحديث الملف الشخصي بنجاح' : 'Profile updated successfully',
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'checkmark-circle',
        theme.colors.success,
      );
      setShowEditModal(false);
      // Reload stats in case org info changed
      refetchStats();
    } catch (error: any) {
      showAlert(
        isArabic ? 'خطأ' : 'Error',
        error.response?.data?.message ||
          (isArabic ? 'فشل تحديث الملف الشخصي' : 'Failed to update profile'),
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'alert-circle',
        theme.colors.error,
      );
    } finally {
      setLoading(false);
    }
  };

  const registerPushNotifications = async () => {
    // Skip notifications on web platform
    if (Platform.OS === 'web') {
      showAlert(
        isArabic ? 'غير متاح' : 'Not Available',
        isArabic
          ? 'الإشعارات غير متوفرة على متصفح الويب. استخدم التطبيق على الهاتف للحصول على الإشعارات.'
          : 'Push notifications are not available on web browsers. Use the mobile app to receive notifications.',
        [{ text: isArabic ? 'حسناً' : 'OK' }],
        'information-circle',
        theme.colors.warning,
      );
      return;
    }

    // ❌ REMOVED: Duplicate registration call (already registered during login)
    // The ProfileScreen should NOT re-register for notifications
    // FCM token is registered ONCE at login in AuthContext
    // This duplicate call was one of 4 causing 6x notifications

    logger.log('✅ Notification preferences screen - FCM token already registered at login');

    showAlert(
      isArabic ? 'معلومة' : 'Info',
      isArabic
        ? 'الإشعارات مفعلة بالفعل. تم تسجيل جهازك عند تسجيل الدخول.'
        : 'Notifications are already enabled. Your device was registered at login.',
      [{ text: isArabic ? 'حسناً' : 'OK' }],
      'checkmark-circle',
      theme.colors.success,
    );
  };

  // 🔄 Manual Update Check Function
  const checkForUpdatesManually = async () => {
    // Skip in development and on web
    if (__DEV__) {
      Alert.alert(
        isArabic ? 'معلومة' : 'Info',
        isArabic
          ? 'التحديثات غير متاحة في وضع التطوير. استخدم التطبيق في وضع الإنتاج.'
          : 'Updates are not available in development mode. Use the app in production mode.',
      );
      return;
    }

    if (Platform.OS === 'web') {
      Alert.alert(
        isArabic ? 'غير متاح' : 'Not Available',
        isArabic
          ? 'التحديثات غير متوفرة على متصفح الويب.'
          : 'Updates are not available on web browsers.',
      );
      return;
    }

    try {
      setLoading(true);

      Alert.alert(
        isArabic ? 'جاري البحث...' : 'Checking...',
        isArabic ? 'جاري البحث عن التحديثات المتاحة...' : 'Checking for available updates...',
      );

      const update = await Updates.checkForUpdateAsync();

      if (update.isAvailable) {
        Alert.alert(
          isArabic ? 'تحديث متاح!' : 'Update Available!',
          isArabic
            ? 'يوجد تحديث جديد متاح. هل تريد تحميله وإعادة تشغيل التطبيق؟'
            : 'A new update is available. Do you want to download and restart the app?',
          [
            { text: isArabic ? 'لاحقاً' : 'Later', style: 'cancel' },
            {
              text: isArabic ? 'تحديث الآن' : 'Update Now',
              onPress: async () => {
                try {
                  Alert.alert(
                    isArabic ? 'جاري التحميل...' : 'Downloading...',
                    isArabic ? 'جاري تحميل التحديث...' : 'Downloading update...',
                  );

                  await Updates.fetchUpdateAsync();

                  Alert.alert(
                    isArabic ? 'تم التحديث!' : 'Updated!',
                    isArabic
                      ? 'تم تحميل التحديث بنجاح. سيتم إعادة تشغيل التطبيق الآن.'
                      : 'Update downloaded successfully. The app will restart now.',
                    [
                      {
                        text: isArabic ? 'إعادة التشغيل' : 'Restart',
                        onPress: () => Updates.reloadAsync(),
                      },
                    ],
                  );
                } catch (error) {
                  logger.error('Failed to download update:', error);
                  Alert.alert(
                    isArabic ? 'خطأ' : 'Error',
                    isArabic ? 'فشل تحميل التحديث' : 'Failed to download update',
                  );
                }
              },
            },
          ],
        );
      } else {
        Alert.alert(
          isArabic ? 'لا توجد تحديثات' : 'No Updates',
          isArabic
            ? 'أنت تستخدم أحدث إصدار من التطبيق ✨'
            : 'You are using the latest version of the app ✨',
        );
      }
    } catch (error) {
      logger.error('Error checking for updates:', error);
      Alert.alert(
        isArabic ? 'خطأ' : 'Error',
        isArabic ? 'فشل البحث عن التحديثات' : 'Failed to check for updates',
      );
    } finally {
      setLoading(false);
    }
  };

  const changeLanguage = async () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    const isRTL = newLang === 'ar';

    showAlert(
      isArabic ? 'تغيير اللغة' : 'Change Language',
      isArabic ? 'سيتم إعادة تشغيل التطبيق لتطبيق التغييرات' : 'App will restart to apply changes',
      [
        { text: isArabic ? 'إلغاء' : 'Cancel', style: 'cancel' },
        {
          text: isArabic ? 'متابعة' : 'Continue',
          onPress: async () => {
            try {
              // Save language to AsyncStorage and change i18n
              await changeAppLanguage(newLang);

              // Force RTL/LTR
              if (I18nManager.isRTL !== isRTL) {
                I18nManager.forceRTL(isRTL);
                I18nManager.allowRTL(isRTL);
              }

              // Reload app
              if (Platform.OS !== 'web') {
                await Updates.reloadAsync();
              } else {
                // On web, just reload the page
                window.location.reload();
              }
            } catch (error) {
              logger.error('Language change failed:', error);
              showAlert(
                isArabic ? 'خطأ' : 'Error',
                isArabic ? 'فشل تغيير اللغة' : 'Failed to change language',
                [{ text: isArabic ? 'حسناً' : 'OK' }],
                'alert-circle',
                theme.colors.error,
              );
            }
          },
        },
      ],
      'language',
      theme.colors.primary,
    );
  };

  return (
    <View
      style={[styles.container, { backgroundColor: isDark ? '#050A30' : theme.colors.background }]}
    >
      {/* Galaxy Background - Full Screen (Visible in Dark Mode OR Always behind header) */}

      <LinearGradient
        colors={['#050A30', '#0b1654', '#1e1b4b']}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={[StyleSheet.absoluteFill, { opacity: isDark ? 1 : 0 }]}
      />

      {/* Stars Layer (Visible in Dark Mode) */}
      {isDark &&
        stars.map((s) => (
          <Animated.View
            key={s.id}
            style={[
              styles.star,
              {
                top: `${s.top}%`,
                left: `${s.left}%`,
                width: s.size,
                height: s.size,
                borderRadius: s.size / 2,
                opacity: twinkleAnim.interpolate({
                  inputRange: [0, 0.5, 1],
                  outputRange: [s.opacity, s.opacity * 0.4, s.opacity],
                }),
              },
            ]}
          />
        ))}

      {/* ✅ Status Bar - Always Light Content (Header is Dark Galaxy) */}
      {/* ✅ ANDROID 15 FIX: Already using transparent + translucent */}
      <StatusBar barStyle="light-content" backgroundColor="transparent" translucent />

      {/* Integrated Glass Header with User Info */}
      <Animated.View
        style={[
          styles.glassHeaderContainer,
          { paddingTop: insets.top, transform: [{ scale: headerScale }] },
        ]}
      >
        {/* Multi-layer Background Gradient */}
        <View style={styles.headerGradient}>
          {/* Primary Gradient Layer - GALAXY THEME */}
          <LinearGradient
            colors={['#050A30', '#0b1654', '#1e1b4b']}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={StyleSheet.absoluteFill}
          />

          {/* Stars Inside Header */}
          {stars.slice(0, 20).map((s) => (
            <Animated.View
              key={`header-star-${s.id}`}
              style={[
                styles.star,
                {
                  top: `${s.top}%`,
                  left: `${s.left}%`,
                  width: s.size,
                  height: s.size,
                  borderRadius: s.size / 2,
                  opacity: twinkleAnim.interpolate({
                    inputRange: [0, 0.5, 1],
                    outputRange: [s.opacity, s.opacity * 0.4, s.opacity],
                  }),
                },
              ]}
            />
          ))}

          {/* Nebula/Wave Effect at Bottom */}
          <View style={styles.waveContainer}>
            <AnimatedWave height={40} amplitude={10} speed={0.3} color="#4f46e5" opacity={0.12} />
            <View style={{ marginTop: -40 }}>
              <AnimatedWave
                height={35}
                amplitude={8}
                speed={0.5}
                color="#a5f3fc"
                opacity={0.08}
                flip
              />
            </View>
          </View>

          {/* Shimmer Effect removed to match Dashboard Header Style */}

          {/* Decorative Pattern removed to match Dashboard Header Style */}
        </View>

        {/* Header Content */}
        <Animated.View style={[styles.headerContent, { opacity: controlsOpacity }]}>
          {/* Top Controls removed from header to move to settings section */}
          <View style={styles.headerTop}>
            <View style={styles.titleContainer}>
              <Ionicons name="person-circle" size={22} color="#ffffff" style={styles.titleIcon} />
              <Text style={styles.headerTitle}>{isArabic ? 'الملف الشخصي' : 'Profile'}</Text>
            </View>
            <View style={styles.headerControls}>
              {/* Empty placeholder to keep title left/right aligned if needed, 
                  or we can remove headerControls entirely. 
                  Keeping it empty for now to maintain layout if flex:space-between is used */}
            </View>
          </View>

          {/* User Profile Section with Glass Effect */}
          {/* ✅ P1-06: Profile header with comprehensive accessibility */}
          <TouchableOpacity
            style={styles.userSection}
            onPress={() => setShowEditModal(true)}
            activeOpacity={0.8}
            accessible={true}
            accessibilityLabel={
              user?.firstName && user?.lastName
                ? `${user.firstName} ${user.lastName}, ${user.email}${
                    user?.roles && user.roles.length > 0
                      ? `, ${isArabic ? 'الأدوار' : 'Roles'}: ${user.roles
                          .slice(0, 2)
                          .map((role) => {
                            const roleName =
                              typeof role === 'string' ? role : role.nameAr || role.name;
                            return isArabic
                              ? getRoleNameArabic(roleName)
                              : getRoleNameEnglish(roleName);
                          })
                          .join(', ')}`
                      : ''
                  }`
                : user?.email
            }
            accessibilityHint={
              isArabic ? 'انقر نقرًا مزدوجًا لتعديل الملف الشخصي' : 'Double tap to edit profile'
            }
            accessibilityRole="button"
          >
            {/* Avatar with Gradient Border */}
            <View style={styles.avatarWrapper}>
              <View style={styles.avatarGradientBorder}>
                <View style={styles.avatarCircle}>
                  <Ionicons name="person" size={32} color="#ffffff" />
                  {/* Online Status Indicator */}
                  <View style={styles.onlineIndicator}>
                    <View style={styles.onlineDot} />
                  </View>
                </View>
              </View>
            </View>

            <View style={styles.userInfo}>
              <Text style={styles.userFullName}>
                {user?.firstName && user?.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user?.email}
              </Text>
              <Text style={styles.userEmailHeader}>{user?.email}</Text>

              {/* Roles with Enhanced Design */}
              {user?.roles && user.roles.length > 0 && (
                <View style={styles.rolesRow}>
                  {user.roles.slice(0, 2).map((role, index) => {
                    const roleName = typeof role === 'string' ? role : role.nameAr || role.name;
                    const roleDisplay = isArabic
                      ? typeof role === 'string'
                        ? getRoleNameArabic(role)
                        : role.nameAr
                      : typeof role === 'string'
                      ? getRoleNameEnglish(role)
                      : role.name;
                    const roleIcon = getRoleIcon(roleName);

                    return (
                      <View key={index} style={styles.roleChip}>
                        <View style={styles.roleIconBg}>
                          <Ionicons name={roleIcon as any} size={12} color="#ffffff" />
                        </View>
                        <Text style={styles.roleChipText}>{roleDisplay}</Text>
                      </View>
                    );
                  })}
                  {user.roles.length > 2 && (
                    <View style={styles.moreRolesBadge}>
                      <Text style={styles.moreRoles}>+{user.roles.length - 2}</Text>
                    </View>
                  )}
                </View>
              )}
            </View>

            {/* Edit Icon with Pulse */}
            <View style={styles.editIconContainer}>
              <View style={styles.editIconPulse} />
              <Ionicons name="create-outline" size={18} color="rgba(255,255,255,0.9)" />
            </View>
          </TouchableOpacity>
        </Animated.View>
      </Animated.View>

      <ScrollView
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefetching}
            onRefresh={onRefresh}
            tintColor={theme.colors.primary}
          />
        }
      >
        {/* User Statistics */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            {isArabic ? 'الإحصائيات' : 'Statistics'}
          </Text>

          {/* BUG-009: Platform admin has no org — show N/A message instead of false zeros */}
          {!user?.orgId ? (
            <View style={[styles.statsGrid, { justifyContent: 'center' }]}>
              <Text
                style={{
                  color: theme.colors.textSecondary,
                  textAlign: 'center',
                  fontSize: 13,
                  flex: 1,
                  paddingVertical: 8,
                }}
              >
                {isArabic
                  ? '— إحصائيات المنظمة غير متاحة لمدير المنصة —'
                  : '— Org statistics not applicable for Platform Admin —'}
              </Text>
            </View>
          ) : (
            <View style={styles.statsGrid}>
              {/* ✅ P1-06: Statistics cards with accessibility */}
              <View
                style={[
                  styles.statCard,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
                accessible={true}
                accessibilityLabel={`${isArabic ? 'الأجهزة' : 'Devices'}, ${
                  userStats.devicesCount
                }`}
                accessibilityRole="text"
              >
                <View style={[styles.statIcon, { backgroundColor: theme.colors.primary + '15' }]}>
                  <Ionicons name="hardware-chip" size={24} color={theme.colors.primary} />
                </View>
                <Text style={[styles.statValue, { color: theme.colors.text }]}>
                  {userStats.devicesCount}
                </Text>
                <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'جهاز' : 'Devices'}
                </Text>
              </View>

              <View
                style={[
                  styles.statCard,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
                accessible={true}
                accessibilityLabel={`${isArabic ? 'أجهزة نشطة' : 'Active devices'}, ${
                  userStats.activeDevicesCount
                }`}
                accessibilityRole="text"
              >
                <View style={[styles.statIcon, { backgroundColor: theme.colors.success + '15' }]}>
                  <Ionicons name="checkmark-circle" size={24} color={theme.colors.success} />
                </View>
                <Text style={[styles.statValue, { color: theme.colors.text }]}>
                  {userStats.activeDevicesCount}
                </Text>
                <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'نشط' : 'Active'}
                </Text>
              </View>

              <View
                style={[
                  styles.statCard,
                  { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                ]}
                accessible={true}
                accessibilityLabel={`${isArabic ? 'تنبيهات نشطة' : 'Active alarms'}, ${
                  userStats.activeAlarmsCount
                }`}
                accessibilityRole="text"
              >
                <View style={[styles.statIcon, { backgroundColor: theme.colors.error + '15' }]}>
                  <Ionicons name="alert-circle" size={24} color={theme.colors.error} />
                </View>
                <Text style={[styles.statValue, { color: theme.colors.text }]}>
                  {userStats.activeAlarmsCount}
                </Text>
                <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'إنذار' : 'Alarms'}
                </Text>
              </View>

              {canSeeStructureStats && (
                <View
                  style={[
                    styles.statCard,
                    { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                  ]}
                  accessible={true}
                  accessibilityLabel={`${isArabic ? 'المواقع' : 'Sites'}, ${userStats.sitesCount}`}
                  accessibilityRole="text"
                >
                  <View style={[styles.statIcon, { backgroundColor: theme.colors.info + '15' }]}>
                    <Ionicons name="location" size={24} color={theme.colors.info} />
                  </View>
                  <Text style={[styles.statValue, { color: theme.colors.text }]}>
                    {userStats.sitesCount}
                  </Text>
                  <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>
                    {isArabic ? 'موقع' : 'Sites'}
                  </Text>
                </View>
              )}

              {canSeeStructureStats && (
                <View
                  style={[
                    styles.statCard,
                    { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
                  ]}
                  accessible={true}
                  accessibilityLabel={`${isArabic ? 'المناطق' : 'Zones'}, ${userStats.zonesCount}`}
                  accessibilityRole="text"
                >
                  <View style={[styles.statIcon, { backgroundColor: theme.colors.warning + '15' }]}>
                    <Ionicons name="apps" size={24} color={theme.colors.warning} />
                  </View>
                  <Text style={[styles.statValue, { color: theme.colors.text }]}>
                    {userStats.zonesCount}
                  </Text>
                  <Text style={[styles.statLabel, { color: theme.colors.textSecondary }]}>
                    {isArabic ? 'منطقة' : 'Zones'}
                  </Text>
                </View>
              )}
            </View>
          )}
        </View>

        {/* Data Management Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            {isArabic ? 'إدارة البيانات' : 'Data Management'}
          </Text>

          <TouchableOpacity
            style={[
              styles.menuItem,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
            onPress={() => navigation.navigate('MeetingRoomSchedule')}
            activeOpacity={0.7}
            accessible={true}
            accessibilityLabel={isArabic ? 'جدول غرف الاجتماعات' : 'Meeting Room Schedule'}
            accessibilityHint={
              isArabic
                ? 'انقر نقرًا مزدوجًا لعرض جدول غرف الاجتماعات'
                : 'Double tap to view meeting room schedule'
            }
            accessibilityRole="button"
          >
            <View style={styles.menuItemLeft}>
              <View style={[styles.iconContainer, { backgroundColor: theme.colors.info + '15' }]}>
                <Ionicons name="calendar" size={22} color={theme.colors.info} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                  {isArabic ? 'جدول غرف الاجتماعات' : 'Meeting Room Schedule'}
                </Text>
                <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                  {isArabic
                    ? 'عرض الحجوزات وحالة الغرف للعميل'
                    : 'View bookings and room availability'}
                </Text>
              </View>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
          </TouchableOpacity>

          {/* Sites Management - Admin & Platform Admin only */}
          {canManageSitesAndZones(user) && (
            <TouchableOpacity
              style={[
                styles.menuItem,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => navigation.navigate('SitesManagement')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={`${isArabic ? 'إدارة المواقع' : 'Sites Management'}, ${
                userStats.sitesCount
              } ${isArabic ? 'موقع' : 'sites'}`}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا للانتقال إلى إدارة المواقع'
                  : 'Double tap to navigate to sites management'
              }
              accessibilityRole="button"
            >
              <View style={styles.menuItemLeft}>
                <View style={[styles.iconContainer, { backgroundColor: theme.colors.info + '15' }]}>
                  <Ionicons name="location" size={22} color={theme.colors.info} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                    {isArabic ? 'إدارة المواقع' : 'Sites Management'}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                    {isArabic ? `${userStats.sitesCount} موقع` : `${userStats.sitesCount} sites`}
                  </Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}

          {/* Zones Management - Admin & Platform Admin only */}
          {canManageSitesAndZones(user) && (
            <TouchableOpacity
              style={[
                styles.menuItem,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => navigation.navigate('ZonesManagement')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={`${isArabic ? 'إدارة المناطق' : 'Zones Management'}, ${
                userStats.zonesCount
              } ${isArabic ? 'منطقة' : 'zones'}`}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا للانتقال إلى إدارة المناطق'
                  : 'Double tap to navigate to zones management'
              }
              accessibilityRole="button"
            >
              <View style={styles.menuItemLeft}>
                <View
                  style={[styles.iconContainer, { backgroundColor: theme.colors.warning + '15' }]}
                >
                  <Ionicons name="apps" size={22} color={theme.colors.warning} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                    {isArabic ? 'إدارة المناطق' : 'Zones Management'}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                    {isArabic ? `${userStats.zonesCount} منطقة` : `${userStats.zonesCount} zones`}
                  </Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}

          {/* Users Management - Admin & Platform Admin only */}
          {canManageUsers(user) && (
            <TouchableOpacity
              style={[
                styles.menuItem,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => navigation.navigate('UsersManagement')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={isArabic ? 'إدارة المستخدمين' : 'Users Management'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا للانتقال إلى إدارة المستخدمين'
                  : 'Double tap to navigate to users management'
              }
              accessibilityRole="button"
            >
              <View style={styles.menuItemLeft}>
                <View
                  style={[styles.iconContainer, { backgroundColor: theme.colors.success + '15' }]}
                >
                  <Ionicons name="people" size={22} color={theme.colors.success} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                    {isArabic ? 'إدارة المستخدمين' : 'Users Management'}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                    {isArabic ? 'إدارة حسابات المستخدمين' : 'Manage user accounts'}
                  </Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}

          {(canManageSitesAndZones(user) || canManageUsers(user)) && (
            <TouchableOpacity
              style={[
                styles.menuItem,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => navigation.navigate('KiosksManagement')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={isArabic ? 'إدارة الأكشاك' : 'Kiosk Management'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا للانتقال إلى إدارة الأكشاك'
                  : 'Double tap to navigate to kiosk management'
              }
              accessibilityRole="button"
            >
              <View style={styles.menuItemLeft}>
                <View
                  style={[styles.iconContainer, { backgroundColor: theme.colors.primary + '15' }]}
                >
                  <Ionicons name="tablet-landscape" size={22} color={theme.colors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                    {isArabic ? 'إدارة الأكشاك' : 'Kiosk Management'}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                    {isArabic
                      ? 'كود التفعيل، التحكم، ولقطات الشاشة'
                      : 'Activation codes, controls, and screenshots'}
                  </Text>
                </View>
              </View>
              <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
            </TouchableOpacity>
          )}

          {/* Reports moved to web — use https://app.iot-edge.io/reports */}

          {/* Show empty state if user has no permissions */}
          {!canManageSitesAndZones(user) && !canManageUsers(user) && !canCreateReports(user) && (
            <View
              style={[
                styles.emptyPermissions,
                { backgroundColor: theme.colors.warning + '10', borderColor: theme.colors.warning },
              ]}
            >
              <Ionicons name="lock-closed" size={32} color={theme.colors.warning} />
              <Text style={[styles.emptyPermissionsText, { color: theme.colors.text }]}>
                {isArabic ? 'لا توجد صلاحيات إدارة' : 'No management permissions'}
              </Text>
              <Text style={[styles.emptyPermissionsSubtext, { color: theme.colors.textSecondary }]}>
                {isArabic
                  ? 'اتصل بالمدير للحصول على صلاحيات إضافية'
                  : 'Contact admin for additional permissions'}
              </Text>
            </View>
          )}
        </View>

        {/* Settings Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            {isArabic ? 'إعدادات التطبيق' : 'App Settings'}
          </Text>

          <TouchableOpacity
            style={[
              styles.menuItem,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
            onPress={changeLanguage}
            activeOpacity={0.7}
            accessible={true}
            accessibilityLabel={`${isArabic ? 'اللغة' : 'Language'}, ${
              isArabic ? 'العربية' : 'English'
            }`}
            accessibilityHint={
              isArabic
                ? 'انقر نقرًا مزدوجًا للتبديل إلى اللغة الإنجليزية'
                : 'Double tap to switch to Arabic'
            }
            accessibilityRole="button"
          >
            <View style={styles.menuItemLeft}>
              <View
                style={[styles.iconContainer, { backgroundColor: theme.colors.primary + '15' }]}
              >
                <Ionicons name="language" size={22} color={theme.colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                  {isArabic ? 'اللغة' : 'Language'}
                </Text>
                <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                  {isArabic ? 'العربية' : 'English'}
                </Text>
              </View>
            </View>
            <View style={styles.menuItemRight}>
              <View style={[styles.badge, { backgroundColor: theme.colors.primary }]}>
                <Text style={styles.badgeText}>{isArabic ? 'EN' : 'ع'}</Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
            </View>
          </TouchableOpacity>

          <View
            style={[
              styles.menuItem,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <View style={styles.menuItemLeft}>
              <View
                style={[styles.iconContainer, { backgroundColor: theme.colors.warning + '15' }]}
              >
                <Ionicons name={isDark ? 'moon' : 'sunny'} size={22} color={theme.colors.warning} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                  {isArabic ? 'المظهر' : 'Appearance'}
                </Text>
                <Text style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                  {isDark
                    ? isArabic
                      ? 'الوضع الليلي'
                      : 'Dark Mode'
                    : isArabic
                    ? 'الوضع النهاري'
                    : 'Light Mode'}
                </Text>
              </View>
            </View>
            <Switch
              value={isDark}
              onValueChange={toggleTheme}
              trackColor={{ false: '#767577', true: theme.colors.primary }}
              thumbColor={isDark ? '#fff' : '#f4f3f4'}
              accessible={true}
              accessibilityLabel={isArabic ? 'الوضع الداكن' : 'Dark mode'}
              accessibilityHint={
                isDark
                  ? isArabic
                    ? 'قيد التشغيل. اضغط للتبديل إلى الوضع الفاتح'
                    : 'On. Tap to switch to light mode'
                  : isArabic
                  ? 'متوقف. اضغط للتبديل إلى الوضع الداكن'
                  : 'Off. Tap to switch to dark mode'
              }
              accessibilityRole="switch"
              accessibilityState={{ checked: isDark }}
            />
          </View>
        </View>

        {/* Account Actions Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            {isArabic ? 'الحساب' : 'Account'}
          </Text>

          <TouchableOpacity
            style={[
              styles.menuItem,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
            onPress={() => navigation.navigate('NotificationSettings')}
            activeOpacity={0.7}
            accessible={true}
            accessibilityLabel={isArabic ? 'إعدادات الإشعارات' : 'Notification Settings'}
            accessibilityHint={
              isArabic
                ? 'انقر نقرًا مزدوجًا لإدارة إعدادات الإشعارات'
                : 'Double tap to manage notification settings'
            }
            accessibilityRole="button"
          >
            <View style={styles.menuItemLeft}>
              <View
                style={[styles.iconContainer, { backgroundColor: theme.colors.primary + '15' }]}
              >
                <Ionicons name="notifications" size={22} color={theme.colors.primary} />
              </View>
              <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                {isArabic ? 'إعدادات الإشعارات' : 'Notification Settings'}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.menuItem,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
            onPress={() => navigation.navigate('TwoFactorSettings')}
            activeOpacity={0.7}
            accessible={true}
            accessibilityLabel={isArabic ? 'المصادقة الثنائية' : 'Two-Factor Authentication'}
            accessibilityHint={
              isArabic
                ? 'انقر نقرًا مزدوجًا لإدارة المصادقة الثنائية'
                : 'Double tap to manage two-factor authentication'
            }
            accessibilityRole="button"
          >
            <View style={styles.menuItemLeft}>
              <View style={[styles.iconContainer, { backgroundColor: '#4CAF50' + '15' }]}>
                <Ionicons name="shield-checkmark" size={22} color="#4CAF50" />
              </View>
              <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                {isArabic ? 'المصادقة الثنائية' : 'Two-Factor Authentication'}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.menuItem,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
            onPress={() => setShowPasswordModal(true)}
            activeOpacity={0.7}
            accessible={true}
            accessibilityLabel={isArabic ? 'تغيير كلمة المرور' : 'Change Password'}
            accessibilityHint={
              isArabic
                ? 'انقر نقرًا مزدوجًا لفتح نموذج تغيير كلمة المرور'
                : 'Double tap to open change password form'
            }
            accessibilityRole="button"
          >
            <View style={styles.menuItemLeft}>
              <View
                style={[styles.iconContainer, { backgroundColor: theme.colors.warning + '15' }]}
              >
                <Ionicons name="key" size={22} color={theme.colors.warning} />
              </View>
              <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                {isArabic ? 'تغيير كلمة المرور' : 'Change Password'}
              </Text>
            </View>
            <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.menuItem,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
            onPress={checkForUpdatesManually}
            activeOpacity={0.7}
            disabled={loading}
            accessible={true}
            accessibilityLabel={isArabic ? 'البحث عن تحديثات' : 'Check for Updates'}
            accessibilityHint={
              isArabic
                ? 'انقر نقرًا مزدوجًا للبحث عن تحديثات التطبيق المتاحة'
                : 'Double tap to check for available app updates'
            }
            accessibilityRole="button"
            accessibilityState={{ disabled: loading }}
          >
            <View style={styles.menuItemLeft}>
              <View
                style={[styles.iconContainer, { backgroundColor: theme.colors.success + '15' }]}
              >
                <Ionicons name="cloud-download" size={22} color={theme.colors.success} />
              </View>
              <Text style={[styles.menuItemText, { color: theme.colors.text }]}>
                {isArabic ? 'البحث عن تحديثات' : 'Check for Updates'}
              </Text>
            </View>
            {loading ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Ionicons name="chevron-forward" size={20} color={theme.colors.textSecondary} />
            )}
          </TouchableOpacity>
        </View>

        {/* App Info Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            {isArabic ? 'معلومات التطبيق' : 'App Info'}
          </Text>

          <View
            style={[
              styles.infoCard,
              { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
            ]}
          >
            <View style={styles.infoItem}>
              <View style={styles.infoItemLeft}>
                <Ionicons name="cube" size={20} color={theme.colors.primary} />
                <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'اسم التطبيق' : 'App Name'}
                </Text>
              </View>
              <Text style={[styles.infoValue, { color: theme.colors.text }]}>
                {Constants.expoConfig?.name || 'IoT Edge'}
              </Text>
            </View>

            <View style={styles.infoItem}>
              <View style={styles.infoItemLeft}>
                <Ionicons name="pricetag" size={20} color={theme.colors.primary} />
                <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'الإصدار' : 'Version'}
                </Text>
              </View>
              <Text style={[styles.infoValue, { color: theme.colors.text }]}>
                {Constants.expoConfig?.version || '2.3.24'}
              </Text>
            </View>

            <View style={styles.infoItem}>
              <View style={styles.infoItemLeft}>
                <Ionicons name="hammer" size={20} color={theme.colors.primary} />
                <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'رقم البناء' : 'Build'}
                </Text>
              </View>
              <Text style={[styles.infoValue, { color: theme.colors.text }]}>
                {Platform.OS === 'ios'
                  ? Constants.expoConfig?.ios?.buildNumber || '40'
                  : Constants.expoConfig?.android?.versionCode || '153'}
              </Text>
            </View>

            <View style={styles.infoItem}>
              <View style={styles.infoItemLeft}>
                <Ionicons name="business" size={20} color={theme.colors.primary} />
                <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'المنظمة' : 'Organization'}
                </Text>
              </View>
              <Text style={[styles.infoValue, { color: theme.colors.text }]} numberOfLines={1}>
                {user?.organization?.name || 'N/A'}
              </Text>
            </View>

            <View style={styles.infoItem}>
              <View style={styles.infoItemLeft}>
                <Ionicons name="globe" size={20} color={theme.colors.primary} />
                <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'البيئة' : 'Environment'}
                </Text>
              </View>
              <Text style={[styles.infoValue, { color: theme.colors.text }]}>
                {__DEV__ ? (isArabic ? 'تطوير' : 'Development') : isArabic ? 'إنتاج' : 'Production'}
              </Text>
            </View>

            <View style={styles.infoItem}>
              <View style={styles.infoItemLeft}>
                <Ionicons name="sync" size={20} color={theme.colors.primary} />
                <Text style={[styles.infoLabel, { color: theme.colors.textSecondary }]}>
                  {isArabic ? 'رقم التحديث' : 'Update ID'}
                </Text>
              </View>
              <Text
                style={[styles.infoValue, { color: theme.colors.text, fontSize: 11 }]}
                numberOfLines={1}
              >
                {Updates.updateId?.substring(0, 8) || (isArabic ? 'غير متاح' : 'N/A')}
              </Text>
            </View>
          </View>

          {/* Additional Info Links */}
          <View style={styles.linkButtons}>
            <TouchableOpacity
              style={[
                styles.linkButton,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => Linking.openURL('https://iot-edge.io')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={isArabic ? 'الموقع الإلكتروني' : 'Website'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لفتح الموقع الإلكتروني في المتصفح'
                  : 'Double tap to open website in browser'
              }
              accessibilityRole="button"
            >
              <Ionicons name="globe-outline" size={20} color={theme.colors.primary} />
              <Text style={[styles.linkButtonText, { color: theme.colors.primary }]}>
                {isArabic ? 'الموقع الإلكتروني' : 'Website'}
              </Text>
              <Ionicons name="open-outline" size={16} color={theme.colors.primary} />
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.linkButton,
                { backgroundColor: theme.colors.card, borderColor: theme.colors.border },
              ]}
              onPress={() => Linking.openURL('mailto:support@iot-edge.io')}
              activeOpacity={0.7}
              accessible={true}
              accessibilityLabel={isArabic ? 'الدعم الفني' : 'Support'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لإرسال بريد إلكتروني للدعم الفني'
                  : 'Double tap to send email to support'
              }
              accessibilityRole="button"
            >
              <Ionicons name="mail-outline" size={20} color={theme.colors.primary} />
              <Text style={[styles.linkButtonText, { color: theme.colors.primary }]}>
                {isArabic ? 'الدعم الفني' : 'Support'}
              </Text>
              <Ionicons name="open-outline" size={16} color={theme.colors.primary} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Logout Button */}
        <TouchableOpacity
          style={[
            styles.logoutButton,
            { backgroundColor: theme.colors.error + '15', borderColor: theme.colors.error },
          ]}
          onPress={handleLogout}
          activeOpacity={0.7}
          accessible={true}
          accessibilityLabel={isArabic ? 'تسجيل الخروج' : 'Logout'}
          accessibilityHint={
            isArabic
              ? 'انقر نقرًا مزدوجًا لتسجيل الخروج من حسابك'
              : 'Double tap to logout from your account'
          }
          accessibilityRole="button"
        >
          <Ionicons name="log-out" size={22} color={theme.colors.error} />
          <Text style={[styles.logoutText, { color: theme.colors.error }]}>
            {isArabic ? 'تسجيل الخروج' : 'Logout'}
          </Text>
        </TouchableOpacity>

        <View style={styles.footer}>
          <Text style={[styles.footerText, { color: theme.colors.textSecondary }]}>
            {isArabic ? 'صُنع بعناية للرعاية الصحية' : 'Built with care for Healthcare'}
          </Text>
        </View>
      </ScrollView>

      {/* Change Password Modal */}
      <Modal visible={showPasswordModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'تغيير كلمة المرور' : 'Change Password'}
              </Text>
              <TouchableOpacity
                onPress={() => setShowPasswordModal(false)}
                accessible={true}
                accessibilityLabel={isArabic ? 'إغلاق' : 'Close'}
                accessibilityHint={
                  isArabic ? 'انقر نقرًا مزدوجًا لإغلاق النموذج' : 'Double tap to close form'
                }
                accessibilityRole="button"
              >
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder={isArabic ? 'كلمة المرور الحالية' : 'Current Password'}
              placeholderTextColor={theme.colors.textSecondary}
              secureTextEntry
              value={currentPassword}
              onChangeText={setCurrentPassword}
              accessible={true}
              accessibilityLabel={isArabic ? 'كلمة المرور الحالية' : 'Current Password'}
              accessibilityHint={
                isArabic ? 'أدخل كلمة المرور الحالية' : 'Enter your current password'
              }
            />

            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder={isArabic ? 'كلمة المرور الجديدة' : 'New Password'}
              placeholderTextColor={theme.colors.textSecondary}
              secureTextEntry
              value={newPassword}
              onChangeText={setNewPassword}
              accessible={true}
              accessibilityLabel={isArabic ? 'كلمة المرور الجديدة' : 'New Password'}
              accessibilityHint={isArabic ? 'أدخل كلمة المرور الجديدة' : 'Enter your new password'}
            />

            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder={isArabic ? 'تأكيد كلمة المرور' : 'Confirm Password'}
              placeholderTextColor={theme.colors.textSecondary}
              secureTextEntry
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              accessible={true}
              accessibilityLabel={isArabic ? 'تأكيد كلمة المرور' : 'Confirm Password'}
              accessibilityHint={
                isArabic
                  ? 'أعد إدخال كلمة المرور الجديدة للتأكيد'
                  : 'Re-enter new password to confirm'
              }
            />

            <TouchableOpacity
              style={[styles.modalButton, { backgroundColor: theme.colors.primary }]}
              onPress={handleChangePassword}
              disabled={loading}
              accessible={true}
              accessibilityLabel={isArabic ? 'تغيير كلمة المرور' : 'Change Password'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لحفظ كلمة المرور الجديدة'
                  : 'Double tap to save new password'
              }
              accessibilityRole="button"
              accessibilityState={{ disabled: loading }}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.modalButtonText}>{isArabic ? 'تغيير' : 'Change'}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal visible={showEditModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: theme.colors.card }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
                {isArabic ? 'تعديل الملف الشخصي' : 'Edit Profile'}
              </Text>
              <TouchableOpacity
                onPress={() => setShowEditModal(false)}
                accessible={true}
                accessibilityLabel={isArabic ? 'إغلاق' : 'Close'}
                accessibilityHint={
                  isArabic ? 'انقر نقرًا مزدوجًا لإغلاق النموذج' : 'Double tap to close form'
                }
                accessibilityRole="button"
              >
                <Ionicons name="close" size={24} color={theme.colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder={isArabic ? 'الاسم الأول' : 'First Name'}
              placeholderTextColor={theme.colors.textSecondary}
              value={firstName}
              onChangeText={setFirstName}
              accessible={true}
              accessibilityLabel={isArabic ? 'الاسم الأول' : 'First Name'}
              accessibilityHint={isArabic ? 'أدخل الاسم الأول' : 'Enter your first name'}
            />

            <TextInput
              style={[
                styles.input,
                {
                  backgroundColor: theme.colors.background,
                  borderColor: theme.colors.border,
                  color: theme.colors.text,
                },
              ]}
              placeholder={isArabic ? 'الاسم الأخير' : 'Last Name'}
              placeholderTextColor={theme.colors.textSecondary}
              value={lastName}
              onChangeText={setLastName}
              accessible={true}
              accessibilityLabel={isArabic ? 'الاسم الأخير' : 'Last Name'}
              accessibilityHint={isArabic ? 'أدخل الاسم الأخير' : 'Enter your last name'}
            />

            <TouchableOpacity
              style={[styles.modalButton, { backgroundColor: theme.colors.primary }]}
              onPress={handleEditProfile}
              disabled={loading}
              accessible={true}
              accessibilityLabel={isArabic ? 'حفظ التعديلات' : 'Save Changes'}
              accessibilityHint={
                isArabic
                  ? 'انقر نقرًا مزدوجًا لحفظ التعديلات على الملف الشخصي'
                  : 'Double tap to save profile changes'
              }
              accessibilityRole="button"
              accessibilityState={{ disabled: loading }}
            >
              {loading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.modalButtonText}>{isArabic ? 'حفظ' : 'Save'}</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Custom Alert Component */}
      <AlertComponent />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },

  // Glass Header Styles with Enhanced Visuals
  glassHeaderContainer: {
    position: 'relative',
    overflow: 'hidden',
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
    elevation: 10,
    shadowColor: '#4f46e5',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
    zIndex: 100,
  },
  headerGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  gradientLayer1: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#0d1b5e',
  },
  gradientLayer2: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#283593',
    opacity: 0.8,
  },
  shimmerLayer: {
    position: 'absolute',
    top: -50,
    left: -50,
    right: -50,
    bottom: -50,
    backgroundColor: '#ffffff',
    transform: [{ rotate: '45deg' }],
  },
  decorativePattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  glowCircle: {
    position: 'absolute',
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: 'rgba(100, 200, 255, 0.15)',
    shadowColor: '#64c8ff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.6,
    shadowRadius: 30,
  },
  floatingDot: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
    shadowColor: '#ffffff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
  },
  waveContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 50,
    justifyContent: 'flex-end',
    opacity: 0.6,
  },
  headerContent: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
  },
  headerTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  titleIcon: {
    opacity: 0.9,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.3,
    textShadowColor: 'rgba(0, 0, 0, 0.2)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  headerControls: {
    flexDirection: 'row',
    gap: 12,
  },
  controlButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.25)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
    position: 'relative',
  },
  controlButtonGlow: {
    elevation: 4,
    shadowColor: '#ffffff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  iconGlow: {
    position: 'absolute',
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  langBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    backgroundColor: '#ff6b6b',
    borderRadius: 8,
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderWidth: 1.5,
    borderColor: '#ffffff',
  },
  langText: {
    fontSize: 9,
    fontWeight: '800',
    color: '#ffffff',
  },
  userSection: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.18)',
    borderRadius: 16,
    padding: 10,
    gap: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
  },
  avatarWrapper: {
    position: 'relative',
  },
  avatarGradientBorder: {
    padding: 2,
    borderRadius: 30,
    backgroundColor: '#64c8ff',
    shadowColor: '#64c8ff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.5,
    shadowRadius: 6,
  },
  avatarCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: 'rgba(255,255,255,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.5)',
    elevation: 3,
    shadowColor: '#64c8ff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.4,
    shadowRadius: 8,
    position: 'relative',
  },
  onlineIndicator: {
    position: 'absolute',
    bottom: 2,
    right: 2,
    backgroundColor: '#ffffff',
    borderRadius: 10,
    padding: 2,
  },
  onlineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#4ade80',
    shadowColor: '#4ade80',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
  },
  userInfo: {
    flex: 1,
    gap: 6,
  },
  userFullName: {
    fontSize: 17,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.2,
    textShadowColor: 'rgba(0, 0, 0, 0.2)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  userEmailHeader: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.85)',
    fontWeight: '500',
  },
  rolesRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  roleChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.22)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 14,
    gap: 6,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.4)',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  roleIconBg: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: 'rgba(255,255,255,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  roleChipText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.2,
  },
  moreRolesBadge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  moreRoles: {
    fontSize: 11,
    fontWeight: '700',
    color: 'rgba(255,255,255,0.85)',
  },
  editIconContainer: {
    position: 'relative',
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.25)',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
  },
  editIconPulse: {
    position: 'absolute',
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255,255,255,0.15)',
  },

  compactHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
  },
  headerLeft: {
    flex: 1,
  },
  screenTitle: {
    fontSize: 24,
    fontWeight: '700',
  },
  headerRight: {
    flexDirection: 'row',
    gap: 8,
  },
  iconButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  langCode: {
    fontSize: 10,
    fontWeight: '700',
    position: 'absolute',
    bottom: 2,
    right: 2,
  },
  userCard: {
    margin: 16,
    padding: 20,
    borderRadius: 16,
    alignItems: 'center',
    borderWidth: 1,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
  },
  avatarContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  userName: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  userEmail: {
    fontSize: 14,
    marginBottom: 10,
  },
  rolesContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  roleBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
  },
  roleText: {
    fontSize: 13,
    fontWeight: '600',
  },
  editHint: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    marginTop: 12,
  },
  editHintText: {
    fontSize: 13,
    fontWeight: '500',
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 12,
  },
  statsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  statCard: {
    flex: 1,
    minWidth: '30%',
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    gap: 8,
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  statValue: {
    fontSize: 24,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 13,
    textAlign: 'center',
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 16,
    marginBottom: 8,
    borderWidth: 1,
  },
  menuItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuItemText: {
    fontSize: 16,
    fontWeight: '500',
  },

  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  badgeText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  menuItemRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  menuItemValue: {
    fontSize: 14,
  },
  infoCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    gap: 14,
  },
  infoItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoItemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  infoLabel: {
    fontSize: 15,
  },
  infoValue: {
    fontSize: 15,
    fontWeight: '600',
    maxWidth: '50%',
  },
  linkButtons: {
    marginTop: 12,
    gap: 8,
  },
  linkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    gap: 8,
  },
  linkButtonText: {
    fontSize: 15,
    fontWeight: '600',
    flex: 1,
    textAlign: 'center',
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 16,
    marginVertical: 8,
    paddingVertical: 16,
    borderRadius: 16,
    gap: 10,
    borderWidth: 1.5,
  },
  logoutText: {
    fontSize: 17,
    fontWeight: '700',
  },
  emptyPermissions: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    borderRadius: 12,
    borderWidth: 1,
    marginHorizontal: 16,
    marginTop: 8,
  },
  emptyPermissionsText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
  },
  emptyPermissionsSubtext: {
    fontSize: 13,
    marginTop: 4,
    textAlign: 'center',
  },
  footer: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  footerText: {
    fontSize: 13,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 20,
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  input: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    fontSize: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  modalButton: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 17,
    fontWeight: '700',
  },
  star: {
    position: 'absolute',
    backgroundColor: '#fff',
    shadowColor: '#fff',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 2,
    elevation: 2,
  },
});
